import { supabase } from '../config/supabase';

export interface AILiteracyQuestion {
  id: string;
  category: string;
  statement: string;
  example: string;
  order_index: number;
}

export interface AILiteracyAttempt {
  id: string;
  user_id: string | null;
  started_at: string;
  completed_at: string | null;
  answers: Record<string, number>; // question_id -> rating (1-5)
  category_scores: Record<string, number>; // category -> average score
  overall_score: number;
  created_at: string;
}

export interface CategoryScore {
  category: string;
  score: number;
  count: number;
}

export interface LiteracyProfile {
  overallScore: number;
  categoryScores: CategoryScore[];
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

class AILiteracyScanApi {
  /**
   * Get all AI literacy scan questions
   */
  async getQuestions(): Promise<AILiteracyQuestion[]> {
    const { data, error } = await supabase
      .from('ai_literacy_questions')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Create a new literacy scan attempt
   */
  async createAttempt(userId: string): Promise<AILiteracyAttempt> {
    const { data, error } = await supabase
      .from('ai_literacy_attempts')
      .insert({
        user_id: userId,
        started_at: new Date().toISOString(),
        answers: {},
        category_scores: {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Submit completed literacy scan
   */
  async submitAttempt(
    attemptId: string,
    answers: Record<string, number>,
    questions: AILiteracyQuestion[]
  ): Promise<AILiteracyAttempt> {
    // Calculate category scores
    const categoryScores: Record<string, { total: number; count: number }> = {};
    
    questions.forEach((question) => {
      const rating = answers[question.id];
      if (rating && rating >= 1 && rating <= 5) {
        if (!categoryScores[question.category]) {
          categoryScores[question.category] = { total: 0, count: 0 };
        }
        categoryScores[question.category].total += rating;
        categoryScores[question.category].count += 1;
      }
    });

    // Calculate averages
    const finalCategoryScores: Record<string, number> = {};
    Object.entries(categoryScores).forEach(([category, data]) => {
      finalCategoryScores[category] = Math.round((data.total / data.count) * 10) / 10;
    });

    // Calculate overall score
    const totalScore = Object.values(answers).reduce((sum, rating) => sum + rating, 0);
    const overallScore = Math.round((totalScore / questions.length) * 10) / 10;

    const { data, error } = await supabase
      .from('ai_literacy_attempts')
      .update({
        completed_at: new Date().toISOString(),
        answers,
        category_scores: finalCategoryScores,
        overall_score: overallScore
      })
      .eq('id', attemptId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get user's literacy scan history
   */
  async getUserAttempts(userId: string): Promise<AILiteracyAttempt[]> {
    const { data, error } = await supabase
      .from('ai_literacy_attempts')
      .select('*')
      .eq('user_id', userId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get a specific attempt with questions
   */
  async getAttemptWithQuestions(attemptId: string): Promise<{
    attempt: AILiteracyAttempt;
    questions: AILiteracyQuestion[];
  }> {
    // Get attempt
    const { data: attempt, error: attemptError } = await supabase
      .from('ai_literacy_attempts')
      .select('*')
      .eq('id', attemptId)
      .single();

    if (attemptError) throw attemptError;

    // Get all questions
    const questions = await this.getQuestions();

    return {
      attempt,
      questions
    };
  }

  /**
   * Generate literacy profile with insights
   */
  generateProfile(attempt: AILiteracyAttempt, questions: AILiteracyQuestion[]): LiteracyProfile {
    const categoryScores: CategoryScore[] = Object.entries(attempt.category_scores).map(([category, score]) => ({
      category,
      score,
      count: questions.filter(q => q.category === category).length
    }));

    // Identify strengths and weaknesses
    const strengths: string[] = [];
    const weaknesses: string[] = [];

    categoryScores.forEach(({ category, score }) => {
      if (score >= 4.0) {
        strengths.push(category);
      } else if (score <= 2.5) {
        weaknesses.push(category);
      }
    });

    // Generate recommendations
    const recommendations: string[] = [];
    
    if (weaknesses.length > 0) {
      recommendations.push(`Focus op het verbeteren van: ${weaknesses.join(', ')}`);
    }
    
    if (attempt.overall_score < 3.0) {
      recommendations.push('Overweeg om een AI-basis cursus te volgen');
    } else if (attempt.overall_score < 4.0) {
      recommendations.push('Verder ontwikkelen van praktische AI-vaardigheden');
    } else {
      recommendations.push('Gevorderde AI-toepassingen en leiderschap in AI-adoptie');
    }

    return {
      overallScore: attempt.overall_score,
      categoryScores,
      strengths,
      weaknesses,
      recommendations
    };
  }
}

export const aiLiteracyScanApi = new AILiteracyScanApi();
